import type {
  Layout,
  LayoutLeaf,
  LayoutObject,
  ResolvedNode,
  SidedPage,
} from "@pdfking/filling-flex"
import type { Degree, Distance, Margins, SpatialRectangle } from "@pdfking/math"
import type { LayerID, PageNumber } from "@pdfking/renderer-api"
import type { ContentRenderer, GrowTemplate } from "@pdfking/templates"
import type { Atom } from "@pdfking/tfunc"

import type { GrowSize } from "#internal"
import type { ContextHook } from "#internal"
import type { DivisionId } from "#internal"
import type { GrowLeaf } from "#internal"
import type { JobDestination } from "#internal"
import type { MediaHook } from "#internal"
import type { MediaSlot } from "#internal"
import type { PageInformations } from "#internal"
import type { PrintMode } from "#internal"
import type { ShapeSequence } from "#internal"
import type { Side } from "#internal"

/** What every drawing on a sheet is given: the sheet, which side of it this
 * is, and the numbering the pages were given once every sheet was built. */
export type PlotCtx = {
  canvas: SpatialRectangle<Distance>
  /** The whole sheet, laid out: what a drawing checks for room among the
   * pages, wherever it hangs. */
  root: ImpositionNode
  side: Side
  /** The absolute number of the page at `path`; nothing for a page that is
   * not printed, which draws as the "×" placeholder. */
  page_number_of: (path: Atom[]) => PageNumber | undefined
  /** An absolute page's media, once per destination printing it. */
  media_of: (page: PageNumber) => MediaSlot[]
  /** Absent when nothing was packed onto sheets: a pass-through page has no
   * signature to belong to, and the hooks that read this don't run there. */
  page_informations: PageInformations | undefined
  plot_measures: boolean
  /** Only the outermost fill cuts: a fill tiled into another draws no
   * crossmarks of its own. */
  plot_crossmarks: boolean
  /** The most a page's media shows past it on each edge; nothing caps it at
   * the page's own share of the sheet. */
  bleed: Margins<Distance> | undefined
  /** What a page's media goes through before it is plotted: the job's
   * `media_processor` hooks, one after the other. */
  media_processor: MediaHook
  /** What the context goes through wherever it is made: the job's
   * `context_processor` hooks, one after the other. */
  context_processor: ContextHook
}

/** Where a drawing draws. `outer` is the node's box, `inner` what its padding
 * leaves of it; a page's inner is the page itself. `from_sheet` reads a box on
 * the sheet in this frame, for a drawing that measures something laid out
 * elsewhere on the sheet. */
export type DrawFrame = {
  outer: SpatialRectangle<Distance>
  inner: SpatialRectangle<Distance>
  from_sheet: (box: SpatialRectangle<Distance>) => SpatialRectangle<Distance>
}

export type Drawable = (node: ImpositionNode, frame: DrawFrame, ctx: PlotCtx) => ShapeSequence

/**
 * Something to draw on a node once the sheet is laid out, on one layer.
 *
 * `rotation` is the turn the node stood at when the drawing was attached: a
 * node turned further since is drawn in the frame it was attached in and
 * turned onto the sheet with it, so a colorbar attached before a sheet is
 * stood up lies along the edge it was drawn on. A drawing with none draws on
 * the sheet as it is, whatever happened to the node.
 */
export type Drawing = {
  layer: LayerID
  draw: Drawable
  rotation: Degree | undefined
}

/** What a page is, beyond where it stands. Its geometry and its side are the
 * resolved page's; this is what the numbering and the name block read. */
export type PageMeta = {
  print_front: boolean
  print_rear: boolean
  page_index: number
  page_total: number
  page_group_id: string | undefined
  /** The piece of the grow the page is; none on a page that is a whole grow
   * laid out as one. */
  leaf: GrowLeaf | undefined
  /** Where each side of the page stands in its grow's reading order; a side
   * with no place is never a page of its own. */
  order_front: number | undefined
  order_rear: number | undefined
  /** The axis the grow's margins are turned over on for this page. */
  flip_margins: "x" | "y" | undefined
  /** What the generated content draws the page as: its grow's renderer. */
  renderer: ContentRenderer
}

/** The grow a component was folded into: what its guides, cuts and names are
 * drawn from. */
export type GrowData = {
  grow: GrowTemplate
  /** Every piece of the grow, in the grow's own frame. */
  leaves: GrowLeaf[]
  /** Laid out as one page the size of the whole grow, its pieces drawn on it,
   * rather than a page per piece. */
  collapsed: boolean
  padding: Margins<Distance>
  security: Margins<Distance>
  title: string | undefined
  subtitle: string | undefined
  print_front: boolean
  print_rear: boolean
  box_opts: GrowSize | undefined
}

export type JobData = {
  destination: JobDestination
  division: DivisionId
  print_mode: PrintMode
}

/** A fill of one job's machine size. */
export type FillData = {
  job: JobData
  /** The padding the fill tiles with — the component's. */
  padding: Margins<Distance>
  /** The border the fill keeps clear of subjects, all the way round. */
  security: Margins<Distance>
}

/** What an imposition hangs on a layout node. One of `page`, `grow`, `fill`
 * says what the node is; a node that is none of them only carries drawings. */
export type ImpositionNodeData = {
  drawings: Drawing[]
  page: PageMeta | undefined
  grow: GrowData | undefined
  fill: FillData | undefined
}

export type ImpositionLayout = Layout<ImpositionNodeData>
export type ImpositionObject = LayoutObject<ImpositionNodeData>
export type ImpositionNode = ResolvedNode<ImpositionNodeData>
export type ImpositionPage = SidedPage<ImpositionNodeData>
export type ImpositionLeaf = LayoutLeaf<ImpositionNodeData>

/** A node that carries nothing yet. */
export const NO_DATA: ImpositionNodeData = {
  drawings: [],
  page: undefined,
  grow: undefined,
  fill: undefined,
}
